import { Controller } from "@hotwired/stimulus"

// Drives the self-serve migration banner on the admin dashboard.
//
// The banner has a single CTA. Clicking it opens a short qualification
// wizard (which system · how big · what matters most). Each answer
// auto-advances; the final step records everything in the background and
// opens the Calendly booking page in a new tab. No page reload.
export default class extends Controller {
  static targets = ["modal", "step", "dot", "back", "summary"]
  static values = { calendlyUrl: String, saveUrl: String, current: Number }

  connect() {
    this.answers = {}
    this.currentValue = 0
  }

  open() {
    this.answers = {}
    this.currentValue = 0
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  // Backdrop click closes; clicks inside the panel are stopped in markup.
  closeFromBackdrop(event) {
    if (event.target === event.currentTarget) this.close()
  }

  select(event) {
    const button = event.currentTarget
    const { question, value } = button.dataset
    this.answers[question] = value

    // Highlight the chosen option within its step, clear siblings.
    const step = button.closest("[data-migration-assistance-target='step']")
    step.querySelectorAll("[data-question]").forEach((el) => {
      el.dataset.selected = el === button ? "true" : "false"
    })

    // Let the selection register visually, then advance.
    setTimeout(() => this.#advance(), 220)
  }

  back() {
    if (this.currentValue > 0) this.currentValue -= 1
  }

  book() {
    this.#save()
    window.open(this.calendlyUrlValue, "_blank", "noopener")
    this.close()
  }

  currentValueChanged() {
    this.stepTargets.forEach((el, i) => {
      el.classList.toggle("hidden", i !== this.currentValue)
    })
    this.dotTargets.forEach((el, i) => {
      el.dataset.active = i <= this.currentValue ? "true" : "false"
    })
    if (this.hasBackTarget) {
      this.backTarget.classList.toggle("invisible", this.currentValue === 0)
    }
    // Last step is the confirmation — reflect their answers back to them.
    if (this.hasSummaryTarget && this.currentValue === this.stepTargets.length - 1) {
      this.#renderSummary()
    }
  }

  #advance() {
    if (this.currentValue < this.stepTargets.length - 1) {
      this.currentValue += 1
    }
  }

  #renderSummary() {
    const bits = [this.answers.crm, this.answers.member_count, this.answers.priority].filter(Boolean)
    this.summaryTarget.textContent = bits.join(" · ")
  }

  #save() {
    if (!this.hasSaveUrlValue) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new FormData()
    Object.entries(this.answers).forEach(([k, v]) => body.append(k, v))
    const headers = { Accept: "application/json" }
    if (token) headers["X-CSRF-Token"] = token
    fetch(this.saveUrlValue, {
      method: "POST",
      headers,
      body,
      credentials: "same-origin",
    }).catch(() => {})
  }
}
